import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Tabs, Typography } from 'antd';

import { ROUTES, buildRoute } from '@/routes';
import { ResultsExportModal } from '@/components/ResultsExportModal';
import { AssessmentStatus } from '@/services/api';
import { containerVariants, itemVariants } from '@/utils';
import { AssessmentsResultsPanel } from './AssessmentsResultsPanel';
import { HtsSubmissionsResultsPanel } from './HtsSubmissionsResultsPanel';
import styles from './AdminAssessments.module.scss';

// Mirrors the API's MAX_RESULTS_EXPORT_IDS (services/resultsExport.ts).
const MAX_EXPORT_SELECTION = 100;

const TAB_PARAM = 'tab';
const ResultsTab = {
  Assessments: 'assessments',
  HtsSubmissions: 'hts-submissions',
} as const;
type ResultsTab = (typeof ResultsTab)[keyof typeof ResultsTab];

export const AdminAssessments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const activeTab: ResultsTab =
    searchParams.get(TAB_PARAM) === ResultsTab.HtsSubmissions ? ResultsTab.HtsSubmissions : ResultsTab.Assessments;

  // Selections persist across pages but reset on tab switch, so an export
  // only covers the tab in view.
  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState<string[]>([]);
  const [selectedHtsSubmissionIds, setSelectedHtsSubmissionIds] = useState<string[]>([]);
  const [exportOpen, setExportOpen] = useState(false);

  // Built on the live URL for the same reason as useResultsListFilters's
  // writeParams — keeps both lists' filter params intact.
  const handleTabChange = (key: string) => {
    const next = new URLSearchParams(window.location.search);
    if (key === ResultsTab.HtsSubmissions) next.set(TAB_PARAM, key);
    else next.delete(TAB_PARAM);
    const query = next.toString();
    void navigate({ search: query ? `?${query}` : '' }, { replace: true });
    setSelectedAssessmentIds([]);
    setSelectedHtsSubmissionIds([]);
  };

  const handleViewAssessment = useCallback(
    (assessmentId: string) => navigate(buildRoute(ROUTES.ADMIN_SESSION_RESULTS, { assessmentId })),
    [navigate],
  );
  const handleViewHtsSubmission = useCallback(
    (submissionId: string) => navigate(buildRoute(ROUTES.ADMIN_HTS_SUBMISSION_RESULTS, { submissionId })),
    [navigate],
  );

  const selectedCount =
    activeTab === ResultsTab.Assessments ? selectedAssessmentIds.length : selectedHtsSubmissionIds.length;

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div className={styles.header} variants={itemVariants}>
        <div>
          <Typography.Title level={2} className={styles.title}>
            Results
          </Typography.Title>
          <Typography.Text type="secondary" className={styles.pageSub}>
            Review completed interview assessments, score breakdowns, and HTS submissions.
          </Typography.Text>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Button
              type="primary"
              disabled={selectedCount === 0 || selectedCount > MAX_EXPORT_SELECTION}
              onClick={() => setExportOpen(true)}
            >
              {selectedCount > MAX_EXPORT_SELECTION
                ? `Export PDF (max ${MAX_EXPORT_SELECTION})`
                : selectedCount > 0
                  ? `Export PDF (${selectedCount})`
                  : 'Export PDF'}
            </Button>
          }
          items={[
            {
              key: ResultsTab.Assessments,
              label: 'Assessments',
              children: (
                <AssessmentsResultsPanel
                  urlPrefix="assessments"
                  onView={handleViewAssessment}
                  rowSelection={{
                    selectedRowKeys: selectedAssessmentIds,
                    onChange: (keys) => setSelectedAssessmentIds(keys as string[]),
                    preserveSelectedRowKeys: true,
                    // Only a finished sitting has results to export.
                    getCheckboxProps: (record) => ({ disabled: record.status !== AssessmentStatus.Completed }),
                  }}
                />
              ),
            },
            {
              key: ResultsTab.HtsSubmissions,
              label: 'HTS Submissions',
              children: (
                <HtsSubmissionsResultsPanel
                  urlPrefix="hts"
                  onView={handleViewHtsSubmission}
                  rowSelection={{
                    selectedRowKeys: selectedHtsSubmissionIds,
                    onChange: (keys) => setSelectedHtsSubmissionIds(keys as string[]),
                    preserveSelectedRowKeys: true,
                  }}
                />
              ),
            },
          ]}
        />
      </motion.div>

      <ResultsExportModal
        open={exportOpen}
        selection={{
          assessmentIds: activeTab === ResultsTab.Assessments ? selectedAssessmentIds : [],
          htsSubmissionIds: activeTab === ResultsTab.HtsSubmissions ? selectedHtsSubmissionIds : [],
        }}
        onClose={() => setExportOpen(false)}
      />
    </motion.div>
  );
};

export default AdminAssessments;
